import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { LogIn, LogOut, User, ShieldCheck } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Link } from "react-router-dom";
import { sfx } from "@/lib/sounds";
import AuthModal from "./AuthModal";

export default function AuthButton() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("login");

  if (loading) {
    return <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse" />;
  }

  if (!user) {
    return (
      <>
          <button
            data-testid="nav-signin"
            onClick={() => { setMode("login"); setOpen(true); sfx.click(); }}
            className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs sm:text-sm font-bold uppercase tracking-wider whitespace-nowrap border-2 border-black blocky-shadow-black hover-blocky transition-transform"
          >
          <LogIn size={14} /> Sign In
        </button>
        <AuthModal open={open} onOpenChange={setOpen} initialMode={mode} />
      </>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="nav-user" onClick={() => sfx.click()} className="flex items-center gap-2 px-2 py-1.5 rounded-full glass hover:border-[#FFBF00]/40">
          <img src={user.avatar} alt="" className="w-7 h-7 rounded-md object-cover" />
          <span className="hidden sm:inline text-sm font-semibold text-white/90 pr-1 max-w-[100px] truncate">
            {user.mc_username || user.name}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 bg-black/95 border border-white/10 text-white p-2 backdrop-blur-xl">
        <div className="px-3 py-2 border-b border-white/10 mb-2">
          <div className="font-semibold truncate">{user.name}</div>
          <div className="text-[11px] text-white/50 truncate">{user.email}</div>
          {user.mc_username && <div className="text-[10px] font-mono text-[#FFBF00] mt-1">MC · {user.mc_username}</div>}
        </div>
        <Link to="/verify" onClick={() => sfx.click()} data-testid="user-menu-verify" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
          <ShieldCheck size={14} /> Verify LostDC ID
        </Link>
        <Link to="/profile" onClick={() => sfx.click()} data-testid="user-menu-profile" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
          <User size={14} /> My Profile
        </Link>
        <Link to={`/users/${user.id}`} onClick={() => sfx.click()} className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
          <User size={14} /> Public Profile
        </Link>
        <Link to="/messages" onClick={() => sfx.click()} data-testid="user-menu-messages" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
          Messages
        </Link>
        <Link to="/follow-requests" onClick={() => sfx.click()} data-testid="user-menu-requests" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
          Follow Requests
        </Link>
        {user.role === "admin" && (
          <Link to="/admin" onClick={() => sfx.click()} className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/5 text-sm">
            Admin Panel
          </Link>
        )}
        <button
          onClick={() => { logout(); sfx.no(); }}
          data-testid="user-menu-logout"
          className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md hover:bg-red-500/10 text-red-400 text-sm"
        >
          <LogOut size={14} /> Sign Out
        </button>
      </PopoverContent>
    </Popover>
  );
}
